/**
 * SystemSwitch — 사이드바 ERP ↔ HR 전환
 *
 * 2026-10-15: 바로 위 브랜드(로고 + MARYHOUSE)와 겹치던 «MH 메리하우스 / 업무 시스템» 칸을 없애고 전환 단추만 남겼다(대표 요청).
 * - 펼침: 사이드바 너비를 꽉 채운 [ERP | HR] 세그먼트. role="tablist" + aria-selected, 좌/우 방향키 (E2E 가 tab «ERP»·«HR» 로 찾는다)
 * - 접힘: 지금 모드 글자 하나짜리 단추 — 누르면 다른 모드로(탭은 숨김 — E2E 가 접힌 상태에서 탭 0개를 확인한다)
 * 모드 전환 시 사이드바 메뉴 트리 전체가 교체된다.
 */
import React, { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/shadcn/button';
import { useSystemMode, SystemMode } from '../../context/SystemModeContext';

const OPTIONS: Array<{ id: SystemMode; label: string; sub: string }> = [
  { id: 'erp', label: 'ERP', sub: '업무 시스템' },
  { id: 'hr', label: 'HR', sub: '인사 시스템' },
];

interface Props {
  collapsed?: boolean;
}

export const SystemSwitch: React.FC<Props> = ({ collapsed = false }) => {
  const { mode, setMode } = useSystemMode();
  const navigate = useNavigate();
  const location = useLocation();
  const pillsRef = useRef<Array<HTMLButtonElement | null>>([]);

  /**
   * 모드 전환 시 해당 모드의 홈 경로로 자동 이동
   *  - HR 선택 + 현재 경로가 /hr/* 이 아니면 → /hr/me
   *  - ERP 선택 + 현재 경로가 /hr/* 이면 → /dashboard
   *  - 이미 해당 모드 경로에 있으면 그대로 유지
   */
  const changeMode = useCallback(
    (next: SystemMode) => {
      if (next === mode) return;
      setMode(next);
      const path = location.pathname;
      const inHrArea = path.startsWith('/hr');
      if (next === 'hr' && !inHrArea) {
        navigate('/hr/me');
      } else if (next === 'erp' && inHrArea) {
        navigate('/dashboard');
      }
    },
    [mode, setMode, location.pathname, navigate],
  );

  const handleKey = useCallback(
    (e: React.KeyboardEvent, idx: number) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const nextIdx = e.key === 'ArrowLeft' ? Math.max(0, idx - 1) : Math.min(OPTIONS.length - 1, idx + 1);
      pillsRef.current[nextIdx]?.focus();
      changeMode(OPTIONS[nextIdx].id);
    },
    [changeMode],
  );

  const current = OPTIONS.find((o) => o.id === mode) ?? OPTIONS[0];

  if (collapsed) {
    const other = OPTIONS.find((o) => o.id !== mode) ?? OPTIONS[1];
    return (
      <div className="ws-seg ws-seg--xs" style={{ alignSelf: 'center' }}>
        <Button
          type="button"
          variant="ghost"
          className="ws-seg-item"
          aria-pressed
          aria-label={`지금 ${current.sub} — ${other.sub}으로 바꾸기`}
          title={`${other.label}(${other.sub})으로 바꾸기`}
          onClick={() => changeMode(other.id)}
        >
          {current.label}
        </Button>
      </div>
    );
  }

  return (
    <div className="ws-seg ws-seg--xs ws-seg--block" role="tablist" aria-label="시스템 모드 선택">
      {OPTIONS.map((o, idx) => {
        const active = mode === o.id;
        return (
          <Button
            key={o.id}
            ref={(el) => {
              pillsRef.current[idx] = el;
            }}
            type="button"
            variant="ghost"
            className="ws-seg-item"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            title={o.sub}
            onClick={() => changeMode(o.id)}
            onKeyDown={(e) => handleKey(e, idx)}
          >
            {o.label}
          </Button>
        );
      })}
    </div>
  );
};

export default SystemSwitch;
